import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/trpc";
import { AlertTriangle, CheckCircle2, Clock3, Inbox, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const statusLabels = {
  new: "New",
  in_review: "In review",
  resolved: "Resolved",
} as const;

const statusStyles = {
  new: "border-[rgba(191,59,80,0.28)] bg-[rgba(131,12,28,0.12)] text-[var(--signal-soft)]",
  in_review: "border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.06)] text-white/78",
  resolved: "border-[rgba(66,153,108,0.3)] bg-[rgba(66,153,108,0.12)] text-[#9ee6b8]",
} as const;

const statusCards = [
  {
    title: "New queue",
    key: "new",
    icon: Inbox,
  },
  {
    title: "Active review",
    key: "in_review",
    icon: Clock3,
  },
  {
    title: "Resolved",
    key: "resolved",
    icon: CheckCircle2,
  },
] as const;

type SubmissionStatus = keyof typeof statusLabels;

type SupportInboxItem = {
  id: number;
  referenceCode: string;
  status: SubmissionStatus;
  issueType: string;
  region: string;
  fullName: string;
  email: string;
  orderReference: string | null;
  pageReference: string | null;
  details: string;
  requesterOpenId: string | null;
  createdAt: number;
  updatedAt: number;
};

export default function SupportInbox() {
  const { user, loading } = useAuth({ redirectOnUnauthenticated: true });
  const [submissions, setSubmissions] = useState<SupportInboxItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  useEffect(() => {
    if (loading || user?.role !== "admin") {
      return;
    }

    let cancelled = false;

    const loadInbox = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const result = await apiClient.support.adminList.query({
          limit: 50,
          refreshToken: Date.now(),
        });
        if (!cancelled) {
          setSubmissions(result as SupportInboxItem[]);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "The inbox could not be loaded.";
          setLoadError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadInbox();

    return () => {
      cancelled = true;
    };
  }, [loading, user?.role]);

  const counts = useMemo(() => {
    return submissions.reduce(
      (acc, item) => {
        acc[item.status] += 1;
        return acc;
      },
      { new: 0, in_review: 0, resolved: 0 },
    );
  }, [submissions]);

  const handleStatusUpdate = async (id: number, status: SubmissionStatus) => {
    setUpdatingId(id);

    try {
      const result = await apiClient.support.updateStatus.mutate({ id, status });
      const updatedSubmission = result.submission as SupportInboxItem;
      setSubmissions((current) => current.map((item) => (item.id === id ? updatedSubmission : item)));
      toast.success("Support status updated.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Status update failed.";
      toast.error(message);
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(124,22,39,0.2),transparent_26%),linear-gradient(180deg,#06080d_0%,#0b0d13_52%,#05070b_100%)] text-white">
        <div className="container py-16">
          <div className="rounded-[1.8rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-8 text-white/72">Loading inbox access…</div>
        </div>
      </main>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <main className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(124,22,39,0.2),transparent_26%),linear-gradient(180deg,#06080d_0%,#0b0d13_52%,#05070b_100%)] text-white">
        <div className="container py-16">
          <div className="rounded-[1.8rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.28)]">
            <ShieldCheck className="h-6 w-6 text-[var(--signal-soft)]" />
            <h1 className="mt-4 font-display text-4xl text-white">Admin access required</h1>
            <p className="mt-4 max-w-2xl text-base leading-8 text-white/68">
              This internal inbox is reserved for signed-in owner or admin review only.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="bg-[var(--signal-soft)] text-white hover:bg-[var(--signal)]">
                <a href="/support">Return to support</a>
              </Button>
              <Button asChild variant="outline" className="border-white/12 text-white/78 hover:bg-white/8 hover:text-white">
                <a href="/">Return home</a>
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(124,22,39,0.2),transparent_26%),linear-gradient(180deg,#06080d_0%,#0b0d13_52%,#05070b_100%)] text-white">
      <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden="true">
        <div className="noise-overlay h-full w-full" />
      </div>

      <div className="relative container py-8 md:py-12">
        <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] shadow-[0_40px_120px_rgba(0,0,0,0.48)] backdrop-blur-xl">
          <section className="border-b border-white/10 px-6 py-6 md:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Internal support inbox</p>
                <h1 className="mt-4 font-display text-4xl text-white md:text-5xl">Stored support submissions for admin review.</h1>
                <p className="mt-4 text-base leading-8 text-white/68">
                  Review incoming support requests, track operational load, and move each request through a simple status trail without exposing internal review details to public visitors.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="border-white/12 text-white/78 hover:bg-white/8 hover:text-white">
                  <a href="/support">Public support page</a>
                </Button>
                <Button asChild className="bg-[var(--signal-soft)] text-white hover:bg-[var(--signal)]">
                  <a href="/">Homepage</a>
                </Button>
              </div>
            </div>
          </section>

          <section className="border-b border-white/10 px-6 py-8 md:px-8">
            <div className="grid gap-4 md:grid-cols-3">
              {statusCards.map((card) => {
                const Icon = card.icon;
                return (
                  <article key={card.key} className="rounded-[1.35rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.22)]">
                    <Icon className="h-5 w-5 text-[var(--signal-soft)]" />
                    <h2 className="mt-4 font-display text-2xl text-white">{card.title}</h2>
                    <p className="mt-3 font-display text-4xl text-white">{counts[card.key]}</p>
                    <p className="mt-3 text-sm leading-7 text-white/64">Current items in this inbox state.</p>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="px-6 py-8 md:px-8 md:py-10">
            {isLoading ? (
              <div className="rounded-[1.5rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-6 text-white/72">Loading stored submissions…</div>
            ) : loadError ? (
              <div className="rounded-[1.5rem] border border-[rgba(191,59,80,0.22)] bg-[rgba(131,12,28,0.12)] p-6 text-white/80">
                <AlertTriangle className="h-5 w-5 text-[var(--signal-soft)]" />
                <p className="mt-4 text-base leading-8">{loadError}</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="rounded-[1.5rem] border border-dashed border-white/12 bg-[rgba(5,7,11,0.42)] p-6 text-white/60">
                No support submissions have been stored yet.
              </div>
            ) : (
              <div className="space-y-5">
                {submissions.map((submission) => (
                  <article key={submission.id} className="rounded-[1.6rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.045),rgba(255,255,255,0.02))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.24)] md:p-7">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-3xl">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.28em] ${statusStyles[submission.status]}`}>
                            {statusLabels[submission.status]}
                          </span>
                          <span className="text-[11px] uppercase tracking-[0.28em] text-white/44">{submission.referenceCode}</span>
                        </div>
                        <h2 className="mt-4 font-display text-3xl text-white">{submission.issueType}</h2>
                        <p className="mt-3 text-sm leading-7 text-white/64">
                          Submitted by {submission.fullName} · {submission.email} · {submission.region}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {(["new", "in_review", "resolved"] as SubmissionStatus[]).map((status) => (
                          <Button
                            key={status}
                            type="button"
                            variant="outline"
                            disabled={submission.status === status || updatingId === submission.id}
                            className="border-white/12 text-white/78 hover:bg-white/8 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => handleStatusUpdate(submission.id, status)}
                          >
                            Mark {statusLabels[status]}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6 grid gap-4 lg:grid-cols-[0.38fr_0.62fr]">
                      <div className="rounded-[1.2rem] border border-white/10 bg-[rgba(7,9,13,0.56)] p-4 text-sm leading-7 text-white/68">
                        <p><span className="text-white">Submitted:</span> {new Date(submission.createdAt).toLocaleString()}</p>
                        <p><span className="text-white">Updated:</span> {new Date(submission.updatedAt).toLocaleString()}</p>
                        <p><span className="text-white">Order reference:</span> {submission.orderReference || "Not provided"}</p>
                        <p><span className="text-white">Affected page:</span> {submission.pageReference || "Not provided"}</p>
                      </div>
                      <div className="rounded-[1.2rem] border border-white/10 bg-[rgba(7,9,13,0.56)] p-4 text-sm leading-8 text-white/68">
                        {submission.details}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
